import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';
import XTermDebugConsole from './XTermDebugConsole';

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}


interface CustomTerminalProps {
  sshHost: string;
  sshUsername: string;
  sshPassword: string;
}

const CustomTerminal: React.FC<CustomTerminalProps> = ({ sshHost, sshUsername, sshPassword }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDebugConsole, setShowDebugConsole] = useState(true);
  const [sessionId, setSessionId] = useState<string>('');
  const [terminalOutput, setTerminalOutput] = useState<string>('');
  const [socket, setSocket] = useState<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-connect to SSH when component mounts
    connectToSSH();
    
    // Cleanup: disconnect when component unmounts
    return () => {
      if (isConnected) {
        axios.post('/ai/api/ai-chat.php', {
          action: 'disconnect'
        }, {
          withCredentials: true
        }).catch(err => console.error('Failed to disconnect:', err));
      }
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages are added
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connectToSSH = async () => {
    try {
      setIsConnecting(true);
      
      // Connect to the same socket.io server as Terminal component
      const newSocket = io('/terminal', { 
        path: '/terminal/socket.io/',
        transports: ['websocket', 'polling']
      });
      
      newSocket.on('connect', () => {
        // Authenticate first
        newSocket.emit('auth', { sessionId: 'omega-ai-session' });
      });
      
      newSocket.on('authenticated', () => {
        setIsConnected(true);
        setIsConnecting(false);
        
        // Don't create a new SSH session, just listen to existing one
        // The Terminal component will create the SSH session
        
        addMessage('assistant', 'What would you like to do today?');
      });
      
      newSocket.on('connected', (data) => {
        setSessionId(data.sessionId || 'shared');
      });
      
      newSocket.on('data', (data: string) => {
        // Parse Claude responses from terminal output
        if (data.includes('Claude') || data.includes('assistant')) {
          // Extract and add as assistant message
          const cleanOutput = data.replace(/\r\n|\r|\n/g, ' ').trim();
          if (cleanOutput && !cleanOutput.includes('root@ubuntu')) {
            addMessage('assistant', cleanOutput);
          }
        }
      });
      
      newSocket.on('disconnect', () => {
        setIsConnected(false);
        addMessage('system', 'Disconnected from terminal');
      });
      
      setSocket(newSocket);
      
    } catch (error: any) {
      addMessage('system', `Connection failed: ${error.message || error}`);
      setIsConnected(false);
      setIsConnecting(false);
    }
  };

  const addMessage = (type: 'user' | 'assistant' | 'system', content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  // Handle terminal output from XTerm
  const handleTerminalOutput = (data: string) => {
    setTerminalOutput(prev => prev + data);
    
    // Parse output for AI responses
    // Look for complete responses between prompts
    const lines = data.split('\n');
    for (const line of lines) {
      // Check if this looks like an AI response
      if (line.trim() && 
          !line.includes('$') && 
          !line.includes('bash:') && 
          !line.includes('root@') &&
          !line.startsWith('[')) {
        // This might be part of an AI response
        // We'll need more sophisticated parsing here
      }
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !isConnected) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Add user message
    addMessage('user', userMessage);

    try {
      // Send message directly to the SSH terminal session via socket
      if (socket && socket.connected) {
        // Send input to terminal where Claude is running
        socket.emit('data', userMessage + '\r');
      } else {
        throw new Error('Not connected to terminal');
      }
    } catch (error: any) {
      addMessage('system', `Error: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Memoize SSH config to prevent unnecessary re-renders
  const sshConfig = useMemo(() => ({
    host: sshHost,
    username: sshUsername,
    password: sshPassword
  }), [sshHost, sshUsername, sshPassword]);

  return (
    <div className="h-full flex bg-omega-darker">
      {/* Main Chat Container */}
      <div className={`flex flex-col ${showDebugConsole ? 'flex-1' : 'w-full'}`}>
        {/* Header */}
        <div className="bg-omega-dark border-b border-omega-accent/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-400' : isConnecting ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
              <h2 className="text-lg font-bold text-white">OMEGA AI Terminal</h2>
              <span className="text-gray-400">|</span>
              <span className="text-gray-300">{sshHost}</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowDebugConsole(!showDebugConsole)}
                className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {showDebugConsole ? 'Hide' : 'Show'} Debug Console
              </button>
              <div className="text-sm text-gray-400">
                {isConnecting ? 'Connecting...' : isConnected ? 'Connected' : 'Disconnected'}
              </div>
            </div>
          </div>
        </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isConnecting && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omega-accent"></div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Initializing OMEGA AI</h3>
              <p className="text-gray-400">Please wait while we establish connection...</p>
            </div>
          </div>
        )}
        
        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-4 ${
              message.type === 'user' 
                ? 'bg-gradient-to-r from-omega-accent to-blue-500 text-white' 
                : message.type === 'assistant'
                ? 'bg-omega-dark border border-omega-accent/30 text-white'
                : 'bg-gray-800 text-gray-300'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">
                  {message.type === 'user' ? 'You' : message.type === 'assistant' ? '🤖 AI' : 'System'}
                </span>
                <span className="text-xs opacity-70">
                  {formatTimestamp(message.timestamp)}
                </span>
              </div>
              <div className="whitespace-pre-wrap break-words">
                {message.content}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-omega-dark border border-omega-accent/30 text-white rounded-lg p-4 max-w-[80%]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">🤖 AI</span>
                <span className="text-xs opacity-70">thinking...</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-omega-accent rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-omega-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-omega-accent rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-omega-accent/30 p-4">
        <div className="flex gap-3">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isConnected ? "Type your message..." : "Connecting to SSH..."}
            disabled={!isConnected || isLoading}
            className="flex-1 bg-omega-dark border border-omega-accent/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-omega-accent resize-none"
            rows={1}
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || !isConnected || isLoading}
            className="px-6 py-3 bg-gradient-to-r from-omega-accent to-green-400 text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 transform shadow-lg"
          >
            Send
          </button>
        </div>
          <div className="text-xs text-gray-500 mt-2">
            Press Enter to send • Shift+Enter for new line
          </div>
        </div>
      </div>

      {/* Debug Terminal */}
      {showDebugConsole && sessionId && isConnected && (
        <div className="w-96">
          <XTermDebugConsole 
            sshConfig={sshConfig}
            sessionId={sessionId}
            onOutput={handleTerminalOutput}
          />
        </div>
      )}
    </div>
  );
};

export default CustomTerminal;